import { useMemo, useEffect, type ComponentType, type ReactElement } from 'react';
import { useCurrentUserResource } from '@jira/router-resources__current-user/src/index.tsx';
import { defaultLocale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useUserSubscriber } from '@atlassian/jira-user-services/src/main.tsx';
import type { Controller, ControllerBasic, ControllerFull, Data } from './types.tsx';

function useCurrentUserWithResource(): Controller {
	const { data, error, loading, refresh } = useCurrentUserResource();
	const {
		atlassianAccountId: accountId,
		isAdmin,
		isAnonymous,
		isSiteAdmin,
		locale = defaultLocale,
		siteAdminStatus,
		userFullname = '',
	} = useTenantContext();

	useEffect(() => {
		// load on client if not loaded as a resource
		if (!data && !loading && !error) {
			refresh();
		}
	}, [data, error, loading, refresh]);

	return useMemo(() => {
		const userData = !isAnonymous ? data : {};

		return {
			data: {
				user: {
					accountId,
					isAdmin,
					isAnonymous,
					isSiteAdmin,
					locale,
					siteAdminStatus,
					userFullname,
					...userData,
				},
			},
			loading,
			error,
		};
	}, [
		accountId,
		data,
		error,
		isAdmin,
		isAnonymous,
		isSiteAdmin,
		loading,
		locale,
		siteAdminStatus,
		userFullname,
	]);
}

const useCurrentUserBasic = (): ControllerBasic => {
	const tenantContext = useTenantContext();

	return useMemo(
		() => ({
			data: {
				user: {
					accountId: tenantContext.atlassianAccountId,
					isAdmin: tenantContext.isAdmin,
					isAnonymous: tenantContext.isAnonymous,
					isSiteAdmin: tenantContext.isSiteAdmin,
					locale: tenantContext.locale || defaultLocale,
					siteAdminStatus: tenantContext.siteAdminStatus,
					userFullname: tenantContext.userFullname || '',
				},
			},
			loading: false,
			error: null,
		}),
		[
			tenantContext.atlassianAccountId,
			tenantContext.isAdmin,
			tenantContext.isAnonymous,
			tenantContext.isSiteAdmin,
			tenantContext.locale,
			tenantContext.siteAdminStatus,
			tenantContext.userFullname,
		],
	);
};

const useCurrentUserFull = (): ControllerFull => {
	const { data: dataBasic } = useCurrentUserBasic();
	const tenantContext = useTenantContext();

	const [{ data, loading, error }, { fetch }] = useUserSubscriber();

	useEffect(() => {
		tenantContext.atlassianAccountId &&
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			fetch({ params: { accountId: tenantContext.atlassianAccountId as string } }, false);
	}, [fetch, tenantContext.atlassianAccountId]);

	return useMemo(
		() => ({
			data: {
				...dataBasic,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				user: {
					...dataBasic.user,
					...data,
				} as Data,
			},
			loading,
			error,
		}),
		[data, dataBasic, error, loading],
	);
};

const useCurrentUserOld = (): Controller =>
	useTenantContext().isAnonymous ? useCurrentUserBasic() : useCurrentUserFull(); // eslint-disable-line react-hooks/rules-of-hooks

export const useCurrentUser: () => Controller = functionWithCondition(
	() => fg('jfp-magma-current-user-resource'),
	useCurrentUserWithResource,
	useCurrentUserOld,
);

export const CurrentUserProvider: ComponentType<{
	children: (arg1: Controller) => ReactElement;
}> = ({ children }) => {
	const result = useCurrentUser();
	return children(result);
};
